import ButtonStates from "./ButtonStates";
import isMobile from "ismobilejs";

/**
 * @class
 * @extends PIXI.Container
 * @memberOf P3
 */
export default class Button extends PIXI.Container {
    /**
     * @param {ButtonStates} states
     */
    constructor(states) {
        super();

        /**
         * @member {Object}
         * @member {signals.Signal} down
         * @member {signals.Signal} up
         * @member {signals.Signal} over
         * @member {signals.Signal} out
         * @member {signals.Signal} click
         * @member {signals.Signal} animate
         */
        this.signals = {
            down: new signals.Signal(),
            up: new signals.Signal(),
            over: new signals.Signal(),
            out: new signals.Signal(),
            click: new signals.Signal(),
            animate: new signals.Signal(),
            focus: new signals.Signal()
        };

        /**
         * @member {boolean}
         */
        this.animate = true;

        /**
         * @member {boolean}
         */
        this.animateIcon = true;

        /**
         * @member {PIXI.Point}
         */
        this.defaultScale = new PIXI.Point(1, 1);

        /**
         * @member {PIXI.Point}
         */
        this.animateOverScale = new PIXI.Point(1.1, 1.1);

        /**
         * @member {PIXI.Point}
         */
        this.animateDownScale = new PIXI.Point(0.9, 0.9);

        /**
         * @member {string}
         */
        this.upSoundName = null;

        /**
         * @member {string}
         */
        this.overSoundName = null;

        /**
         * @member {string}
         */
        this.downSoundName = null;

        /**
         * @member {string}
         */
        this.clickSoundName = null;

        /**
         * @member {ButtonStates}
         * @protected
         */
        this._states = states;

        /**
         * @member {PIXI.Sprite}
         * @protected
         */
        this._background = new PIXI.Sprite(this._states.normal);
        this._background.anchor = new PIXI.Point(0.5, 0.5);
        this.addChild(this._background);

        /**
         * @member {PIXI.Sprite}
         * @protected
         */
        this._icon = new PIXI.Sprite(this._states.icon || PIXI.Texture.EMPTY);
        this._icon.anchor = new PIXI.Point(0.5, 0.5);
        this.addChild(this._icon);

        /**
         * @member {PIXI.Sprite}
         * @protected
         */
        this._focus = new PIXI.Sprite(this._states.focus || PIXI.Texture.EMPTY);
        this._focus.anchor = new PIXI.Point(0.5, 0.5);
        this._focus.visible = false;
        this.addChild(this._focus);

        /**
         * @member {PIXI.Sprite}
         * @protected
         */
        this._shortcutIcon = new PIXI.Sprite(PIXI.Texture.EMPTY);
        this._shortcutIcon.anchor = new PIXI.Point(0.5, 0.5);
        this._background.addChild(this._shortcutIcon);

        /**
         * @member {PIXI.Texture}
         * @protected
         */
        this._currentNormalTexture = this._states.normal;

        /**
         * @member {PIXI.Texture}
         * @protected
         */
        this._currentOverTexture = this._states.over;

        /**
         * @member {PIXI.Texture}
         * @protected
         */
        this._currentDownTexture = this._states.down;

        /**
         * @member {PIXI.Texture}
         * @protected
         */
        this._currentIconTexture = this._states.icon;

        /**
         * @member {PIXI.Texture}
         * @protected
         */
        this._currentIconOverTexture = this._states.iconOver;

        /**
         * @member {PIXI.Texture}
         * @protected
         */
        this._currentIconDownTexture = this._states.iconDown;

        /**
         * @member {TweenMax}
         * @protected
         */
        this._tweenOver = null;

        /**
         * @member {TweenMax}
         * @protected
         */
        this._tweenOut = null;

        /**
         * @member {TweenMax}
         * @protected
         */
        this._tweenDown = null;

        /**
         * @member {boolean}
         * @protected
         */
        this._enabled = true;

        /**
         * @member {boolean}
         * @private
         */
        this._focused = false;

        this.interactive = true;
        this.buttonMode = true;
        this.mousedown = this.touchstart = this._onMouseDown.bind(this);
        this.mouseup = this.touchend = this._onMouseUp.bind(this);
        this.mouseupoutside = this.touchendoutside = this._onMouseOut.bind(this);
        this.click = this.tap = this._onMouseClick.bind(this);
        this.mouseover = this._onMouseOver.bind(this);
        this.mouseout = this._onMouseOut.bind(this);
    }

    /**
     * @param {PIXI.Texture} normal
     * @param {PIXI.Texture} [over]
     * @param {PIXI.Texture} [down]
     * @param {PIXI.Texture} [icon]
     */
    static create(normal, over, down, icon) {
        const states = new ButtonStates(normal, over, down, icon);
        return new Button(states);
    }

    /**
     */
    destroy() {
        TweenMax.killTweensOf(this);
        TweenMax.killTweensOf(this.scale);

        this.signals.up.dispose();
        this.signals.down.dispose();
        this.signals.over.dispose();
        this.signals.out.dispose();
        this.signals.click.dispose();
        this.signals.animate.dispose();
        this.signals.focus.dispose();

        super.destroy({children: true});
    }

    focus() {
        this._focused = true;
        this._focus.visible = true;

    }

    unfocus() {
        this._focused = false;
        this._focus.visible = false;
    }

    /**
     * @param {PIXI.Texture} texture
     * @param {PIXI.Point} offset
     */
    setShortcutIcon(texture, offset) {
        this._shortcutIcon.texture = texture;
        this._shortcutIcon.position.set(offset.x, offset.y);
    }

    /**
     * @param {Event} event
     * @protected
     */
    _onMouseDown(event) {
        this._states.down && (this._background.texture = this._currentDownTexture);
        this._states.iconDown && (this._icon.texture = this._currentIconDownTexture);

        if (this.animate) {
            const tweens = TweenMax.getTweensOf(this, true);
            const a = tweens.indexOf(this._tweenOver) > -1 ? tweens.length - 1 : tweens.length;
            if (!a) {
                if (this._tweenOver) {
                    this._tweenOver.kill();
                    this._tweenOver = null;
                }
                this._tweenDown = this.animateDown();
            }
        }
        (Button.audio && this.downSoundName) && Button.audio.playSound(this.downSoundName);
        this._enabled && this.signals.down.dispatch(this, event);
    }

    /**
     * @param {Event} event
     * @protected
     */
    _onMouseUp(event) {
        this._background.texture = (this._states.over && !isMobile.any) ? this._currentOverTexture : this._currentNormalTexture;
        if (this._states.iconOver && !isMobile.any) {
            this._icon.texture = this._currentIconOverTexture;
        } else if (this._states.icon) {
            this._icon.texture = this._currentIconTexture;
        }
        if (this.animate && this._tweenDown) {
            if (this._tweenDown) {
                this._tweenDown.kill();
                this._tweenDown = null;
            }
            this._tweenOver = !isMobile.any ? this.animateOver() : this.animateOut();
        }
        (Button.audio && this.upSoundName) && Button.audio.playSound(this.upSoundName);
        this._enabled && this.signals.up.dispatch(this, event);
    }

    /**
     * @param {Event} event
     * @protected
     */
    _onMouseOver(event) {
        this._states.over && (this._background.texture = this._currentOverTexture);
        this._states.iconOver && (this._icon.texture = this._currentIconOverTexture);

        if (this.animate && !isMobile.any) {
            const tweens = TweenMax.getTweensOf(this, true);
            if (tweens.indexOf(this._tweenOver) === -1) {
                if (this._tweenOut) {
                    this._tweenOut.kill();
                    this._tweenOut = null;
                }
                this._tweenOver = this.animateOver();
            }
        }
        if (this.animateIcon && !isMobile.any) {
            TweenMax.killTweensOf(this._icon.scale);
            TweenMax.to(this._icon.scale, 0.8, {
                x: 1.2,
                y: 1.2,
                ease: Elastic.easeOut,
                easeParams: [1.6]
            });
        }
        (Button.audio && this.overSoundName) && Button.audio.playSound(this.overSoundName);
        this._enabled && this.signals.over.dispatch(this, event);
    }

    /**
     * @param {Event} event
     * @protected
     */
    _onMouseOut(event) {
        this._background.texture = this._currentNormalTexture;
        this._states.icon && (this._icon.texture = this._currentIconTexture);

        if (this._tweenOver || this._tweenDown) {
            const tweens = TweenMax.getTweensOf(this, true);
            if (tweens.indexOf(this._tweenOut) === -1) {
                if (this._tweenOver) {
                    this._tweenOver.kill();
                    this._tweenOver = null;
                }
                this._tweenOut = this.animateOut();
            }
            if (tweens.indexOf(this._tweenDown) === -1) {
                if (this._tweenDown) {
                    this._tweenDown.kill();
                    this._tweenDown = null;
                }
                this._tweenOut = this.animateOut();
            }
        }
        TweenMax.killTweensOf(this._icon.scale);
        TweenMax.to(this._icon.scale, 0.34, {
            x: 1,
            y: 1,
            ease: Power1.easeInOut
        });
        (Button.audio && this.upSoundName) && Button.audio.playSound(this.upSoundName);
        this._enabled && this.signals.out.dispatch(this, event);
    }

    /**
     * @param {Event} event
     * @protected
     */
    _onMouseClick(event) {
        (Button.audio && this.clickSoundName) && Button.audio.playSound(this.clickSoundName);
        this._enabled && this.signals.click.dispatch(this, event);
    }

    /**
     * @returns {TweenMax}
     */
    animateOver() {
        return TweenMax.to(this.scale, 0.6, {
            x: this.defaultScale.x * this.animateOverScale.x,
            y: this.defaultScale.y * this.animateOverScale.y,
            ease: Elastic.easeOut,
            easeParams: [1],
            onComplete: function() {
                this.signals.animate.dispatch(this, "over");
            },
            onCompleteScope: this
        });
    };

    /**
     * @returns {TweenMax}
     */
    animateOut() {
        return TweenMax.to(this.scale, 0.3, {
            x: this.defaultScale.x,
            y: this.defaultScale.y,
            ease: Back.easeInOut,
            easeParams: [2],
            onComplete: function() {
                this.signals.animate.dispatch(this, "out");
            },
            onCompleteScope: this
        });
    };

    /**
     * @returns {TweenMax}
     */
    animateDown() {
        return TweenMax.to(this.scale, 0.14, {
            x: this.defaultScale.x * this.animateDownScale.x,
            y: this.defaultScale.y * this.animateDownScale.y,
            ease: Back.easeOut,
            easeParams: [1],
            onComplete: function() {
                this.signals.animate.dispatch(this, "down");
            },
            onCompleteScope: this
        });
    };

    /**
     */
    stopAnimations() {
        const tweens = TweenMax.getTweensOf(this, true);
        if (tweens.indexOf(this._tweenOver) === -1) {
            if (this._tweenOut) {
                this._tweenOut.kill();
                this._tweenOut = null;
            }
            this._tweenOver = this.animateOver();
        }
        if (tweens.indexOf(this._tweenOut) === -1) {
            if (this._tweenOver) {
                this._tweenOver.kill();
                this._tweenOver = null;
            }
        }
        if (tweens.indexOf(this._tweenDown) === -1) {
            if (this._tweenDown) {
                this._tweenDown.kill();
                this._tweenDown = null;
            }
        }
    };

    /**
     * @returns {ButtonStates}
     */
    get states() {
        return this._states;
    }

    /**
     * @returns {PIXI.Texture}
     */
    get currentNormalTexture() {
        return this._currentNormalTexture;
    }

    /**
     * @returns {PIXI.Texture}
     */
    get currentOverTexture() {
        return this._currentOverTexture;
    }

    /**
     * @returns {PIXI.Texture}
     */
    get currentDownTexture() {
        return this._currentDownTexture;
    }

    /**
     * @returns {PIXI.Texture}
     */
    get currentIconTexture() {
        return this._currentIconTexture;
    }

    /**
     * @returns {boolean}
     */
    get enabled() {
        return this._enabled;
    }

    /**
     * @param {boolean} value
     */
    set enabled(value) {
        this._enabled = value;
        this._currentNormalTexture = (value || !this._states.normalInactive) ? this._states.normal : this._states.normalInactive;
        this._currentOverTexture = (value || !this._states.overInactive) ? this._states.over : this._states.overInactive;
        this._currentDownTexture = (value || !this._states.downInactive) ? this._states.down : this._states.downInactive;
        this._currentIconTexture = (value || !this._states.iconInactive) ? this._states.icon : this._states.iconInactive;
        this._currentIconOverTexture = (value || !this._states.iconOverInactive) ? this._states.iconOver : this._states.iconOverInactive;
        this._currentIconDownTexture = (value || !this._states.iconDownInactive) ? this._states.iconDown : this._states.iconDownInactive;

        this._currentNormalTexture && (this._background.texture = this._currentNormalTexture);
        this._currentIconTexture && (this._icon.texture = this._currentIconTexture);
    }

    /**
     * @returns {boolean}
     */
    get focused() {
        return this._focused;
    }

    /**
     * @returns {number}
     */
    get tint() {
        return this._background.tint;
    }

    /**
     * @param {number} value
     */
    set tint(value) {
        this._background.tint = value;
        this._icon.tint = value;
    }
}